import { useState, useRef, useEffect, useMemo } from 'react';

const FacetCloseIcon = () => (
    <svg
        className="ms-facet-icon"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        aria-hidden="true"
        focusable="false"
    >
        <path
            d="M2.15 1.35L6 5.2l3.85-3.85.8.8L6.8 6l3.85 3.85-.8.8L6 6.8l-3.85 3.85-.8-.8L5.2 6 1.35 2.15l.8-.8z"
            fill="currentColor"
        />
    </svg>
);

export default function Sidebar({
    title,
    allItems,
    filteredItems = [],
    visibleItemsCount,
    proximitySearchMessage = '',
    selectedRegione,
    selectedProvincia,
    selectedTipologia,
    regioni,
    province,
    tipologie,
    searchText,
    searchCap,
    capSearchError,
    isCapIndexLoaded,
    useMyLocation,
    onRegioneSelect,
    onTipologiaSelect,
    onFreeSearch,
    onAutocompleteSelect,
    onCapSearch,
    onUseMyLocationToggle,
    onProvinciaSelect,
    onReset,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);

    const searchRef = useRef(null);
    const autocompleteRef = useRef(null);

    function getAddress(item) {
        return item['indirizzo completo'] || item.indirizzo || '';
    }

    function getStructureType(item) {
        return item['tipologia struttura'] || item.tipologia || item.ente || '';
    }

    function escapeRegExp(str) {
        return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    function renderHighlightedText(text, query) {
        if (!text) return null;

        const normalizedQuery = (query || '').trim();

        if (normalizedQuery.length < 2) {
            return text;
        }

        const safe = escapeRegExp(normalizedQuery);
        const regex = new RegExp(`(${safe})`, 'ig');
        const parts = text.split(regex);

        return parts.map((part, i) => (
            i % 2 === 1
                ? <mark key={i} className="ms-typeahead-mark">{part}</mark>
                : <span key={i}>{part}</span>
        ));
    }

    const tipologieOptions = useMemo(() => {
        if (!selectedTipologia || tipologie.includes(selectedTipologia)) {
            return tipologie;
        }

        return [selectedTipologia, ...tipologie];
    }, [selectedTipologia, tipologie]);

    const autocompleteItems = useMemo(() => {
        const q = searchText.trim().toLowerCase();

        if (q.length < 2) {
            return [];
        }

		// DEBUG: log dei dati usati per l'autocomplete
        if (allItems.length > 0 && q.length >= 2) {
            console.log('🔍 Autocomplete Debug:', {
                allItemsCount: allItems.length, 
                searchQuery: q,
                firstItem: allItems[0],
                itemKeys: Object.keys(allItems[0] || {}),
            });
        }

        return allItems
            .filter(item => {
                if (
                    selectedRegione &&
                    (!item.regione || item.regione.toLowerCase() !== selectedRegione.toLowerCase())
                ) {
                    return false;
                }

                if (
                    selectedProvincia &&
                    (!item.provincia || item.provincia.toLowerCase() !== selectedProvincia.toLowerCase())
                ) {
                    return false;
                }

                if (
                    selectedTipologia &&
                    getStructureType(item).toLowerCase() !== selectedTipologia.toLowerCase()
                ) {
                    return false;
                }

                return true;
            })
            .filter(item => {
                const denominazione = (item.denominazione || '').toLowerCase();
                const indirizzo = getAddress(item).toLowerCase();

                return (
                    denominazione.includes(q) ||
                    indirizzo.includes(q)
                );
            })
            .map(item => ({
                item,
                denominazione: item.denominazione || getAddress(item) || '',
                indirizzo: getAddress(item),
            }))
            .filter(suggestion => suggestion.denominazione || suggestion.indirizzo)
            .slice(0, 100);
    }, [allItems, searchText, selectedRegione, selectedProvincia, selectedTipologia]);

    function handleSearchInput(e) {
        onFreeSearch(e.target.value);
        setIsAutocompleteOpen(true);
    }

    function handleSuggestionClick(suggestion) {
        const value = suggestion.denominazione || suggestion.indirizzo || '';

        if (suggestion.item && onAutocompleteSelect) {
            onAutocompleteSelect(suggestion.item, value);
        } else {
            onFreeSearch(value);
        }

        setIsAutocompleteOpen(false);
    }

    useEffect(() => {
        function handleClick(e) {
            if (autocompleteRef.current && !autocompleteRef.current.contains(e.target)) {
                setIsAutocompleteOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClick);

        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const showAutocomplete = isAutocompleteOpen && autocompleteItems.length > 0 && searchText.trim().length >= 2;
    const showCapHint = searchCap.length > 0 && searchCap.length < 5 && !capSearchError;
    const hasConcatenableFacet = selectedRegione || selectedProvincia || selectedTipologia || searchText;
    const hasAnyFacet = hasConcatenableFacet || searchCap || useMyLocation;
    const displayedItemsCount = Number.isFinite(visibleItemsCount)
        ? visibleItemsCount
        : filteredItems.length;

    return (
        <>
            <div className={`ms-col-left${isOpen ? ' ms-col-left--open' : ''}`}>
                <div className="ms-navigation-header" onClick={onReset} role="button">
					<img src="/Content/img/sisalute.png" alt="SiSalute" className="ms-ss-logo" />
                    <h2 dangerouslySetInnerHTML={{ __html: title }} className="lh-1 fs-5 fs-sm-4 fs-lg-3" />
                </div>

                <div className="ms-search-area p-3">
                    <div className="ms-search-wrapper mb-3 position-relative" ref={autocompleteRef}>
                        <label className="form-label fw-semibold mb-1">Cerca struttura</label>

                        <div className="position-relative">
                            <input
                                ref={searchRef}
                                type="text"
                                className="form-control"
                                placeholder="Inserisci nome o indirizzo"
                                value={searchText}
                                onChange={handleSearchInput}
                                onFocus={() => setIsAutocompleteOpen(true)}
                            />
                        </div>

                        {showAutocomplete && ( 
                            <ul className="ms-autocomplete list-unstyled mb-0">
                                {autocompleteItems.map((s, i) => (
                                    <li key={`${s.denominazione}-${s.indirizzo}-${i}`}>
                                        <button
                                            type="button"
                                            className="btn btn-link rounded-0 text-start w-100 py-2"
                                            onClick={() => handleSuggestionClick(s)}
                                        >
                                            <span className="d-block ms-autocomplete-title">
                                                {renderHighlightedText(s.denominazione, searchText)}
                                            </span>

                                            {s.indirizzo && (
                                                <small className="d-block ms-autocomplete-address">
                                                    {renderHighlightedText(s.indirizzo, searchText)}
                                                </small>
                                            )}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="mb-3">
                        <label className="form-label fw-semibold mb-1">Regione</label>
                        <select
                            className="form-select"
                            value={selectedRegione || ''}
                            onChange={e => onRegioneSelect(e.target.value || null)}
                        >
                            <option value="">Tutte le regioni</option>
                            {regioni.map(r => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </select>
                    </div>

                    {selectedRegione && (
                        <div className="mb-3">
                            <label className="form-label fw-semibold mb-1">Provincia</label>
                            <select
                                className="form-select"
                                value={selectedProvincia || ''}
                                onChange={e => onProvinciaSelect(e.target.value || null)}
                            >
                                <option value="">Tutte le province</option>
                                {province.map(p => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="mb-3">
                        <label className="form-label fw-semibold mb-1">Tipologia struttura</label>
                        <select
                            className="form-select"
                            value={selectedTipologia || ''}
                            onChange={e => onTipologiaSelect(e.target.value || null)}
                        >
                            <option value="">Tutte le tipologie</option>
                            {tipologieOptions.map(t => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div> 

                    <div className="ms-cap-wrapper mb-2">
                        <label className="form-label fw-semibold mb-1 d-flex align-items-center gap-1">
                            CAP di interesse 
                            <span
                                className="ms-cap-tooltip"
                                data-bs-toggle="tooltip"
                                title="Inserisci un CAP italiano"
                            >
                                <span className="fa fa-info-circle text-primary" />
                            </span>
                        </label>

                        <div className="mb-2">
                            <input
                                type="text"
                                className={`form-control${capSearchError ? ' is-invalid' : ''}`}
                                placeholder="Es. 00162"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={5}
                                value={searchCap}
                                onChange={e => onCapSearch(e.target.value)}
                                aria-invalid={!!capSearchError}
                                aria-describedby="ms-cap-help"
                            />

                            {showCapHint && (
                                <div id="ms-cap-help" className="form-text">
                                    Inserisci 5 cifre per avviare la ricerca di prossimità.
                                </div>
                            )}

                            {!isCapIndexLoaded && !capSearchError && (
                                <div className="form-text">
                                    Archivio CAP in caricamento…
                                </div>
                            )}

                            {capSearchError && (
                                <div id="ms-cap-help" className="invalid-feedback d-block">
                                    {capSearchError}
                                </div>
                            )}
                        </div>

                        <div className="mt-3 form-check form-switch d-flex align-items-center justify-content-between px-0">
                            <label className="form-check-label fw-semibold" htmlFor="ms-use-location">
                                Oppure usa la tua posizione attuale
                            </label>

                            <input
                                id="ms-use-location"
                                className="form-check-input ms-0"
                                type="checkbox"
                                checked={useMyLocation}
                                onChange={e => onUseMyLocationToggle(e.target.checked)}
                            />
                        </div>
                    </div>

                    {hasAnyFacet && (
                        <div className="ms-active-facets pt-2">
                            {/*<h5 className="mb-0 text-secondary fw-semibold fs-6 text-uppercase" style={{ flex: "0 0 100%" }}>*/}
                            {/*    <i className="fa fa-filter me-1" aria-hidden="true"></i> Filtri attivi:*/}
                            {/*</h5>*/}
                            {searchText && (
                                <button
                                    type="button"
                                    className="ms-facet badge rounded-pill bg-info"
                                    onClick={() => onFreeSearch('')}
                                >
                                    Ricerca testuale
                                    <FacetCloseIcon />
                                </button>
                            )}

                            {selectedRegione && (
                                <button
                                    type="button"
                                    className="ms-facet badge rounded-pill bg-info"
                                    onClick={() => onRegioneSelect(null)}
                                >
                                    Regione: {selectedRegione}
                                    <FacetCloseIcon />
                                </button>
                            )}

                            {selectedProvincia && (
                                <button
                                    type="button"
                                    className="ms-facet badge rounded-pill bg-info"
                                    onClick={() => onProvinciaSelect(null)}
                                >
                                    Provincia: {selectedProvincia}
                                    <FacetCloseIcon />
                                </button>
                            )}

                            {selectedTipologia && (
                                <button
                                    type="button"
                                    className="ms-facet badge rounded-pill bg-info"
                                    onClick={() => onTipologiaSelect(null)}
                                >
                                    Tipologia: {selectedTipologia}
                                    <FacetCloseIcon />
                                </button>
                            )}

                            {useMyLocation ? (
                                <button
                                    type="button"
                                    className="ms-facet badge rounded-pill bg-info"
                                    onClick={() => onUseMyLocationToggle(false)}
                                >
                                    La tua posizione
                                    <FacetCloseIcon />
                                </button>
                            ) : searchCap ? (
                                <button
                                    type="button"
                                    className="ms-facet badge rounded-pill bg-info"
                                    onClick={() => onCapSearch('')}
                                >
                                    CAP: {searchCap}
                                    <FacetCloseIcon />
                                </button>
                            ) : null}
                            <h5 className={`mt-3 text-secondary text-${proximitySearchMessage ? 'start' : 'end'} fs-6`} style={{ flex: "0 0 100%" }}>
                                {proximitySearchMessage ? (
                                    <div className="d-flex">
                                        <i className="fa fa-info-circle me-2 fs-3 mt-0" aria-hidden="true"></i>
                                        {proximitySearchMessage}
                                    </div>
                                ) : (
                                    <>
                                        <span className="fw-semibold">{displayedItemsCount}</span> strutture trovate
                                    </>
                                )}
                            </h5>
                        </div>
                    )}
                </div>

                <button
                    className="ms-mobile-handler btn btn-primary d-lg-none rounded-0"
                    onClick={() => setIsOpen(o => !o)}
                    aria-label="Apri navigazione"
                >
                    <span className={`fa fa-caret-${isOpen ? 'left' : 'right'} fa-lg`} />
                </button>
            </div>
        </>
    );
}
